import { ArrowRight, CheckCircle2, ExternalLink } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import healthchecksHeroImg from "@/assets/healthchecks-hero.jpg";
import goteborgsklinikenLogo from "@/assets/goteborgskliniken-logo.svg";
import { PageMeta } from "@/components/site/PageMeta";

const Halsokontroller = () => {
  const { t, lang } = useLang();
  const h = t.healthChecks;


  const title = lang === "sv" ? "Hälsokontroller | Intygsläkaren" : "Health checks | Intygsläkaren";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={h.subtitle} />
      <Header />
      <main>
        {/* Hero */}
        <PageHeroCard
          imageSrc={healthchecksHeroImg}
          imageAlt={h.title}
          compact
          overlay={
            <img
              src={goteborgsklinikenLogo}
              alt="Göteborgskliniken"
              className="pointer-events-none absolute bottom-5 right-5 z-10 h-8 w-auto sm:bottom-8 sm:right-10 sm:h-12"
              style={{ filter: "brightness(0) invert(1) sepia(1) saturate(3) hue-rotate(135deg) brightness(0.18)" }}
            />
          }
        >
          <div className="animate-fade-up max-w-2xl">
            <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {h.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {h.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild variant="hero" size="xl">
                <a href="https://goteborgskliniken.se/halsokontroller/" target="_blank" rel="noopener noreferrer">
                  {h.bookAtPartner}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        {/* Partnership */}
        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-[1fr_auto]">
              <div>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                  {h.partnerTitle}
                </h2>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{h.partnerText1}</p>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">{h.partnerText2}</p>
                <Button asChild variant="hero" size="lg" className="mt-6">
                  <a href="https://goteborgskliniken.se/halsokontroller/" target="_blank" rel="noopener noreferrer">
                    {h.bookAtPartner}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Sections of checks */}
        {h.sections.map((section) => (
          <section key={section.title} className="pb-12 lg:pb-16">
            <div className="container-wide">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                {section.title}
              </h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {section.items.map((item) => (
                  <div
                    key={item.name}
                    className="relative flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card sm:rounded-3xl"
                  >
                    {item.popular && (
                      <span className="absolute -top-3 right-4 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-card">
                        {h.popular}
                      </span>
                    )}
                    <h3 className="font-display text-lg font-semibold text-foreground">{item.name}</h3>
                    {item.price && <p className="mt-1 text-sm font-semibold text-primary">{item.price}</p>}
                    <p className="mt-2 text-sm text-muted-foreground">{item.desc}</p>
                    <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                      {item.points.map((p) => (
                        <li key={p} className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild variant="soft" size="sm" className="mt-5 w-fit">
                      <a href={item.href} target="_blank" rel="noopener noreferrer">
                        {h.bookCta}
                        <ExternalLink className="ml-1 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* How it works */}
        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {h.howTitle}
              </h2>
              <p className="mt-3 text-muted-foreground">{h.howSubtitle}</p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {h.howSteps.map((s, i) => (
                <div key={s.title} className="rounded-2xl border border-border bg-card p-6 shadow-card sm:rounded-3xl">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
                    {i + 1}
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="relative max-w-2xl">
                <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{h.ctaTitle}</h2>
                <p className="mt-3 text-base text-primary-foreground/85">{h.ctaText}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild variant="onPrimary" size="xl">
                    <a href="https://goteborgskliniken.se/halsokontroller/" target="_blank" rel="noopener noreferrer">
                      {h.ctaPrimary}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild variant="ghostOnPrimary" size="xl">
                    <a href="https://goteborgskliniken.se/kontakt/" target="_blank" rel="noopener noreferrer">
                      {h.ctaSecondary}
                    </a>
                  </Button>
                </div>
              </div>
            </div>
        </section>
        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.nav.checkups },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Halsokontroller;